import React, { Fragment } from 'react';
import { useSetRecoilState } from 'recoil';

import { Button } from '@/components/button';
import { DirectionsPrimary } from '@/components/modules/route/directions/primary';
import { DirectionsRoad } from '@/components/modules/route/directions/road';
import { DirectionsTransfer } from '@/components/modules/route/directions/transfer';
import { MODE_ROAD, MODE_TRANSFER } from '@/lib/constants';
import { OptionModelSequence, SegmentLinkModel, SegmentTransferModel } from '@/lib/models/segment/types';
import { lockedDialogIsOpenState } from '@/state/user';
import { SegmentModeType } from '@/types/api-types';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface RouteDirectionsProps {
  arrivalPortId?: string;
  departurePortId?: string;
  end?: SegmentLinkModel;
  endTransfer?: SegmentTransferModel;
  isLocked?: boolean;
  onClick: () => void;
  routeMode: SegmentModeType;
  segments: OptionModelSequence;
  start?: SegmentLinkModel;
  startTransfer?: SegmentTransferModel;
}

const RouteDirections = ({
  arrivalPortId,
  departurePortId,
  end,
  endTransfer,
  isLocked,
  onClick,
  routeMode,
  segments,
  start,
  startTransfer,
}: RouteDirectionsProps) => {
  const setLockedDialogIsOpen = useSetRecoilState(lockedDialogIsOpenState);
  const isRoadOnly = segments.length === 1 && segments[0].routeType === MODE_ROAD;
  const filteredSegments = !isRoadOnly ? segments.filter((s) => s.routeType !== MODE_ROAD) : segments;
  const hasMoreDetails = routeMode !== MODE_ROAD; // false for road for now until we have road operators
  const color = getTransportColor(routeMode);

  const { cardContainer, buttonHolder, base } = styles();
  const trackClickEvent = (buttonIsLocked: boolean) => {
    trackUser.event('See Route Schedules/Segment', {
      destination: arrivalPortId,
      isLocked: buttonIsLocked,
      origin: departurePortId,
    });
  };

  const handleClick = (): void => {
    if (hasMoreDetails) {
      if (isLocked) {
        setLockedDialogIsOpen(true);
        trackClickEvent(true);
      } else {
        onClick();
        trackClickEvent(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (['Enter', ' '].includes(e.key)) {
      handleClick();
    }
  };

  const renderPrimarySegments = (nextMode: SegmentModeType, segment: SegmentLinkModel | SegmentTransferModel) => {
    switch (segment.routeType) {
      case MODE_TRANSFER:
        return <DirectionsTransfer segment={segment} nextMode={nextMode} />;
      default:
        return <DirectionsPrimary segment={segment as SegmentLinkModel} />;
    }
  };

  return (
    <div className={base()}>
      {start?.routeType === MODE_ROAD && !isRoadOnly && <DirectionsRoad segment={start} isPickup />}
      <div
        className={cardContainer()}
        data-testid="details"
        onClick={handleClick}
        onKeyUp={handleKeyPress}
        role="button"
        style={{ borderBottom: color ? `2px solid ${color}` : undefined }}
        tabIndex={0}
      >
        {startTransfer && <DirectionsTransfer segment={startTransfer} nextMode={segments[0].routeType} />}
        {filteredSegments.map((segment, index) => {
          const nextMode = filteredSegments[index + 1]?.routeType;
          return <Fragment key={segment.id}>{renderPrimarySegments(nextMode, segment)}</Fragment>;
        })}
        {hasMoreDetails && (
          <div className={buttonHolder()}>
            <Button size="small" iconRight={isLocked ? 'lock' : 'arrow-right-alt'}>
              More details
            </Button>
          </div>
        )}
        {endTransfer && end && <DirectionsTransfer segment={endTransfer} nextMode={end.routeType} />}
      </div>
      {end?.routeType === MODE_ROAD && !isRoadOnly && <DirectionsRoad segment={end} />}
    </div>
  );
};

const styles = tv({
  slots: {
    buttonHolder: 'p-4 md:absolute md:right-4 md:top-1/2 md:-translate-y-1/2',
    cardContainer: 'relative z-3 cursor-pointer bg-white shadow-card hover:shadow-softer',
    base: 'border-t border-grey-300 bg-grey-100 px-2 py-4',
  },
});

export { RouteDirections };
